import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import * as XLSX from "xlsx";
import styles from "./ReportBuilder.module.css";
import { getallRegref } from "../../redux/actions";

const camposIniciales = [
  "fecha",
  "placa",
  "pesoneto",
  "acomulado",
  "polucion",
  "observaciones",
  "descripcion_estado",
  "nombre_usuario_m",
  "nombre_usuario_r",
  "origen_material_nombre",
  "material",
  "cliente",
  "lugar",
  "tamano_mate",
  "norma",
  "ubicacion"
];

const ReportBuilder = () => {
  const dispatch = useDispatch();
  const allregref = useSelector((state) => state.allregref);

  const [camposDisponibles, setCamposDisponibles] = useState(camposIniciales);
  const [camposSeleccionados, setCamposSeleccionados] = useState([]);
  const [datos, setDatos] = useState([]);

  // 🔹 Filtros
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  useEffect(() => {
    dispatch(getallRegref());
  }, [dispatch]);

  // 📦 Mover campos entre listas
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const newDisponibles = Array.from(camposDisponibles);
    const newSeleccionados = Array.from(camposSeleccionados);

    let movedItem;
    if (source.droppableId === "disponibles") {
      movedItem = newDisponibles[source.index];
      newDisponibles.splice(source.index, 1);
      if (destination.droppableId === "seleccionados") {
        newSeleccionados.splice(destination.index, 0, movedItem);
      } else {
        newDisponibles.splice(destination.index, 0, movedItem);
      }
    } else {
      movedItem = newSeleccionados[source.index];
      newSeleccionados.splice(source.index, 1);
      if (destination.droppableId === "disponibles") {
        newDisponibles.splice(destination.index, 0, movedItem);
      } else {
        newSeleccionados.splice(destination.index, 0, movedItem);
      }
    }

    setCamposDisponibles(newDisponibles);
    setCamposSeleccionados(newSeleccionados);
  };

  // 🧾 Generar reporte filtrado
  const generarReporte = () => {
    if (!allregref || allregref.length === 0) return;

    let registrosAplanados = allregref.flatMap((ref) =>
      ref.Registros.map((r) => ({
        ...r,
        material: ref.material,
        cliente: ref.cliente,
        lugar: ref.lugar,
        tamano_mate: ref.tamano_mate,
        norma: ref.norma,
        ubicacion:ref.ubicacion
      }))
    );

    // ✅ Filtrar por cliente
    if (clienteFiltro.trim() !== "") {
      registrosAplanados = registrosAplanados.filter((r) =>
        r.cliente?.toLowerCase().includes(clienteFiltro.toLowerCase())
      );
    }

    // ✅ Filtrar por rango de fechas
    if (fechaInicio && fechaFin) {
      registrosAplanados = registrosAplanados.filter((r) => {
        const fecha = new Date(r.fecha);
        return fecha >= new Date(fechaInicio) && fecha <= new Date(fechaFin);
      });
    }

    setDatos(registrosAplanados);
  };

  // 🧮 Exportar a Excel
  const exportarExcel = () => {
    if (datos.length === 0) return;
    const dataFiltrada = datos.map((fila) => {
      const nuevaFila = {};
      camposSeleccionados.forEach((campo) => {
        nuevaFila[campo] = fila[campo] ?? "";
      });
      return nuevaFila;
    });

    const ws = XLSX.utils.json_to_sheet(dataFiltrada);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte");
    XLSX.writeFile(wb, "reporte.xlsx");
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Generador de Informes</h2>

      {/* 🎯 Filtros */}
      <div className={styles.filters}>
        <div className={styles.filterItem}>
          <label>Cliente:</label>
          <input
            type="text"
            value={clienteFiltro}
            onChange={(e) => setClienteFiltro(e.target.value)}
            placeholder="Buscar por cliente"
          />
        </div>

        <div className={styles.filterItem}>
          <label>Desde:</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </div>

        <div className={styles.filterItem}>
          <label>Hasta:</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </div>
      </div>

      {/* 🧱 Sección drag & drop */}
      <div className={styles.columns}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="disponibles">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={styles.box}
              >
                <h3>Campos disponibles</h3>
                {camposDisponibles.map((campo, index) => (
                  <Draggable key={campo} draggableId={campo} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`${styles.item} ${
                          snapshot.isDragging ? styles.dragging : ""
                        }`}
                      >
                        {campo}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          <Droppable droppableId="seleccionados">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={styles.box}
              >
                <h3>Campos seleccionados</h3>
                {camposSeleccionados.map((campo, index) => (
                  <Draggable key={campo} draggableId={campo} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`${styles.itemSelected} ${
                          snapshot.isDragging ? styles.dragging : ""
                        }`}
                      >
                        {campo}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* 🧮 Botones */}
      <div className={styles.actions}>
        <button onClick={generarReporte}>Generar informe</button>
        {datos.length > 0 && (
          <button onClick={exportarExcel}>Exportar a Excel</button>
        )}
      </div>

      {/* 📊 Tabla dinámica */}
      {datos.length > 0 && camposSeleccionados.length > 0 && (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {camposSeleccionados.map((campo) => (
                  <th key={campo}>{campo}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.map((fila, i) => (
                <tr key={i}>
                  {camposSeleccionados.map((campo) => (
                    <td key={campo}>{fila[campo] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportBuilder;
